// client/views/OntologyViewer.tsx
// Ontology Viewer with Sankey graph and interactive detail panel

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAsyncState } from '../hooks/useAsyncState';
import { SankeyGraph } from '../components/SankeyGraph';
import { GraphExplainPanel } from '../components/GraphExplainPanel';
import { OntologyFilters } from '../components/OntologyFilters';
import { OntologyLegend } from '../components/OntologyLegend';
import { OntologyChat } from '../components/OntologyChat';
import { applyFilters, type OntologyFilters as FilterType } from '../utils/ontologyFilters';

interface OntologyNodeMeta {
  score?: number;
  pillar?: string;
  [key: string]: unknown;
}

interface OntologyLinkMeta {
  edgeType?: string;
  [key: string]: unknown;
}

interface OntologyProfile {
  name?: string;
  version?: string;
}

interface OntologyCheck {
  id: string;
  name: string;
  passed: boolean;
  message?: string;
  proof_ref?: string;
  target?: string;
  status?: string;
  result?: string;
  category?: string;
  detail?: string;
  shapeId?: string;
  [key: string]: unknown;
}

interface OntologyStructural {
  totalClasses?: number;
  totalProperties?: number;
  totalIndividuals?: number;
  [key: string]: unknown;
}

interface OntologyRunLog {
  timestamp?: string;
  steps?: Array<{ name: string; duration: number }>;
  ontology_profile?: OntologyProfile;
  ontology_profile_hash?: string;
  shapes_version?: string;
  job_id?: string;
  analyzed_at?: string;
  entries?: unknown[];
  [key: string]: unknown;
}

type OntologyData = {
  graph_schema: string;
  graph: {
    nodes: Array<{ id: string; name: string; kind: string; meta?: OntologyNodeMeta }>;
    links: Array<{ source: string; target: string; value: number; meta?: OntologyLinkMeta }>;
  };
  checks: OntologyCheck[];
  structural: OntologyStructural;
  run_log: OntologyRunLog;
  namespaces?: string[];
  ontology_profile?: { name: string; version: string };
  shapes_version?: string;
};

export function OntologyViewer() {
  const { job_id: jobId } = useParams<{ job_id: string }>();
  const { data, loading, error } = useAsyncState<OntologyData>(
    async () => {
      if (!jobId) return undefined;
      const res = await fetch(`/api/analysis/${jobId}/ontology-graph`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    [jobId]
  );
  const [focusNode, setFocusNode] = useState<{ id: string; name: string; kind: string; meta?: OntologyNodeMeta } | null>(null);
  const [filters, setFilters] = useState<FilterType>({
    provider: 'all',
    matchType: 'all',
  });
  const [chatOpen, setChatOpen] = useState(false);

  if (loading) {
    return (
      <div className="ontology-viewer loading">
        <div className="spinner">Loading ontology graph...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ontology-viewer error">
        <h2>Error Loading Ontology</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ontology-viewer empty">
        <p>No ontology data available for this job.</p>
      </div>
    );
  }

  // Apply filters to graph
  const filteredGraph = applyFilters(data.graph, filters);

  return (
    <div className="ontology-viewer">
      <header className="ontology-header">
        <div className="ontology-header-top">
          <div>
            <h1>Ontology Mappings</h1>
            <p className="job-id">Job ID: {jobId}</p>
            {data.graph_schema && (
              <span className="schema-version">Schema v{data.graph_schema}</span>
            )}
          </div>
          <button
            className="ask-fair-ai-button"
            onClick={() => setChatOpen(true)}
            data-action="open-chat"
          >
            Ask FAIR AI
          </button>
        </div>

        {/* v3 Ontology version badge */}
        <div className="text-xs text-gray-600 mt-2">
          Ontology: {data?.ontology_profile?.version ?? data?.run_log?.ontology_profile?.version ?? 'unknown'}
          {(data?.shapes_version || data?.run_log?.shapes_version) && (
            <> • Shapes: {data?.shapes_version || data?.run_log?.shapes_version}</>
          )}
          {Array.isArray(data?.namespaces) && data.namespaces.length > 0 && (
            <div className="mt-1">
              <span className="font-medium">Namespaces:</span>{" "}
              {data.namespaces.slice(0, 4).join(", ")}
              {data.namespaces.length > 4 && " …"}
            </div>
          )}
        </div>
      </header>

      <div className="ontology-controls">
        <OntologyFilters filters={filters} onChange={setFilters} />
        <OntologyLegend />
      </div>

      <div className="ontology-content">
        {filteredGraph.nodes.length === 0 ? (
          <div className="empty-state">
            <p>No mappings match the selected filters.</p>
          </div>
        ) : (
          <>
            <SankeyGraph
              graph={filteredGraph}
              onNodeClick={setFocusNode}
              focusNodeId={focusNode?.id}
            />

            <GraphExplainPanel
              node={focusNode}
              onClose={() => setFocusNode(null)}
              checks={data.checks}
              structural={data.structural}
              runLog={data.run_log}
            />
          </>
        )}
      </div>

      <div className="ontology-stats">
        <div className="stat">
          <span className="label">Total Columns:</span>
          <span className="value">
            {data.graph.nodes.filter(n => n.kind === 'column').length}
          </span>
        </div>
        <div className="stat">
          <span className="label">Mapped Terms:</span>
          <span className="value">
            {data.graph.nodes.filter(n => n.kind === 'term').length}
          </span>
        </div>
        <div className="stat">
          <span className="label">Namespaces:</span>
          <span className="value">
            {data.graph.nodes.filter(n => n.kind === 'namespace').length}
          </span>
        </div>
        <div className="stat">
          <span className="label">Filtered:</span>
          <span className="value">
            {filteredGraph.nodes.length} / {data.graph.nodes.length} nodes
          </span>
        </div>
      </div>

      {/* Ontology Profile Metadata - Shows what validation rules were applied */}
      <details className="ontology-profile-info">
        <summary className="profile-info-summary">
          <h3>Profile & Validation Metadata</h3>
          <span className="profile-info-toggle">Click to expand</span>
        </summary>
        <div className="profile-grid">
          <div className="profile-item">
            <span className="profile-label">Ontology Profile:</span>
            <span className="profile-value">
              {data?.ontology_profile?.name || data?.run_log?.ontology_profile?.name || 'Unknown'}
              {' '}
              <strong>{data?.ontology_profile?.version || data?.run_log?.ontology_profile?.version || '?'}</strong>
            </span>
          </div>

          {(data?.run_log?.ontology_profile_hash || data?.run_log?.shapes_version) && (
            <div className="profile-item">
              <span className="profile-label">Profile Hash:</span>
              <span className="profile-value font-mono">
                {data?.run_log?.ontology_profile_hash || 'N/A'}
              </span>
            </div>
          )}

          {data?.run_log?.shapes_version && (
            <div className="profile-item">
              <span className="profile-label">Shapes Version:</span>
              <span className="profile-value">{data.run_log.shapes_version}</span>
            </div>
          )}

          {Array.isArray(data?.namespaces) && data.namespaces.length > 0 && (
            <div className="profile-item full-width">
              <span className="profile-label">Namespaces Found in Data ({data.namespaces.length}):</span>
              <div className="namespaces-list">
                {data.namespaces.map((ns, i) => (
                  <code key={i} className="namespace-tag">{ns}</code>
                ))}
              </div>
              <div className="profile-note">
                <small>The namespaces shown above are only those actually used in this dataset's mappings.</small>
              </div>

              {/* Expandable section showing all allowed namespaces from profile v3.1 */}
              <details className="allowed-namespaces-details">
                <summary className="allowed-namespaces-summary">
                  View All Allowed Namespaces in Profile v3.1 (50+)
                </summary>
                <div className="allowed-namespaces-content">
                  <p className="allowed-namespaces-intro">
                    Profile v3.1 allows the following namespaces. Your data may use any of these:
                  </p>
                  <div className="namespaces-list">
                    {/* FAIR / metadata vocabularies */}
                    <code className="namespace-tag">http://purl.obolibrary.org/obo/</code>
                    <code className="namespace-tag">http://www.w3.org/ns/dcat#</code>
                    <code className="namespace-tag">http://purl.org/dc/terms/</code>
                    <code className="namespace-tag">http://www.w3.org/ns/prov#</code>
                    <code className="namespace-tag">https://schema.org/</code>
                    <code className="namespace-tag">https://w3id.org/fair-compass/</code>

                    {/* Life-science identifiers (specific collections) */}
                    <code className="namespace-tag">http://identifiers.org/ncbigene/</code>
                    <code className="namespace-tag">https://identifiers.org/ncbigene/</code>
                    <code className="namespace-tag">http://identifiers.org/ensembl/</code>
                    <code className="namespace-tag">https://identifiers.org/ensembl/</code>
                    <code className="namespace-tag">http://identifiers.org/hgnc/</code>
                    <code className="namespace-tag">https://identifiers.org/hgnc/</code>
                    <code className="namespace-tag">http://identifiers.org/uniprot/</code>
                    <code className="namespace-tag">https://identifiers.org/uniprot/</code>
                    <code className="namespace-tag">http://identifiers.org/chebi/</code>
                    <code className="namespace-tag">https://identifiers.org/chebi/</code>
                    <code className="namespace-tag">http://identifiers.org/pubchem.compound/</code>
                    <code className="namespace-tag">https://identifiers.org/pubchem.compound/</code>
                    <code className="namespace-tag">http://identifiers.org/pubchem.substance/</code>
                    <code className="namespace-tag">https://identifiers.org/pubchem.substance/</code>
                    <code className="namespace-tag">http://identifiers.org/go/</code>
                    <code className="namespace-tag">https://identifiers.org/go/</code>
                    <code className="namespace-tag">http://identifiers.org/mondo/</code>
                    <code className="namespace-tag">https://identifiers.org/mondo/</code>
                    <code className="namespace-tag">http://identifiers.org/ncbitaxon/</code>
                    <code className="namespace-tag">https://identifiers.org/ncbitaxon/</code>
                    <code className="namespace-tag">http://identifiers.org/pubmed/</code>
                    <code className="namespace-tag">https://identifiers.org/pubmed/</code>

                    {/* SPDX + persistent IDs */}
                    <code className="namespace-tag">https://spdx.org/licenses/</code>
                    <code className="namespace-tag">https://doi.org/</code>
                    <code className="namespace-tag">https://orcid.org/</code>
                    <code className="namespace-tag">https://ror.org/</code>
                    <code className="namespace-tag">https://n2t.net/ark:/</code>

                    {/* EBI RDF (narrowed subpaths) */}
                    <code className="namespace-tag">http://rdf.ebi.ac.uk/resource/ensembl/</code>
                    <code className="namespace-tag">http://rdf.ebi.ac.uk/resource/chembl/</code>
                    <code className="namespace-tag">http://rdf.ebi.ac.uk/resource/uniprot/</code>
                  </div>
                  <p className="allowed-namespaces-note">
                    <strong>Security Note:</strong> v3.1 enforces granular namespace control. Only specific identifiers.org collections
                    are allowed (no root access), and ORCID/ROR/HGNC have validation rules applied.
                  </p>
                </div>
              </details>
            </div>
          )}

          {data?.run_log?.analyzed_at && (
            <div className="profile-item">
              <span className="profile-label">Analyzed:</span>
              <span className="profile-value">
                {new Date(data.run_log.analyzed_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </details>

      {/* Chat Panel - Slide in from right */}
      {chatOpen && jobId && (
        <div className="ontology-chat-panel">
          <OntologyChat
            jobId={jobId}
            onClose={() => setChatOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
